import React, { useState } from 'react';
import Papa from 'papaparse';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAll';
import { getCurrentUser } from '../../lib/auth';

// ═══════════════════════════════════════════════════════════════════════════
// Date parsing — one free-text column, four shapes the sheet actually uses:
//
//   1. "Aug 15, 2026"                → single date
//   2. "Aug 10-14, 2026"             → same-month range
//   3. "Apr 29–May 2, 2026"          → cross-month range (note: en dash)
//   4. "Aug 12, 2026, 6pm"           → single date + time (time is stripped,
//                                      never parsed into a field — nothing
//                                      here stores time-of-day)
//
// Ranges use EITHER a plain hyphen (-) or an en dash (–) — the character
// class below matches both, so a range never silently collapses into a
// single-day event just because of which dash character was typed.
//
// Anything that doesn't cleanly match one of these four shapes is left
// unparsed on purpose. Real examples that exist in this sheet and get
// skipped rather than guessed: "Sep 2026" (no day), "Year-round", "TBC
// 2026", "Annual — March/April", "Recurring", "Jul 18, 2026-Jan 2027"
// (spans more than a range), "Oct 8–24, 2026 (est.)" (uncertain by the
// sheet's own admission). None of these have one correct date to guess.
// ═══════════════════════════════════════════════════════════════════════════

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_RE = MONTHS.join('|');
const DASH = '[-\u2013]'; // hyphen or en dash

const RE_SINGLE      = new RegExp(`^(${MONTH_RE}) (\\d{1,2}), (\\d{4})(?:,.*)?$`, 'i');
const RE_SAME_MONTH   = new RegExp(`^(${MONTH_RE}) (\\d{1,2})${DASH}(\\d{1,2}), (\\d{4})(?:\\s*\\(.*\\))?$`, 'i');
const RE_CROSS_MONTH  = new RegExp(`^(${MONTH_RE}) (\\d{1,2})${DASH}(${MONTH_RE}) (\\d{1,2}), (\\d{4})(?:\\s*\\(.*\\))?$`, 'i');

function toISO(monthAbbr, day, year) {
  const mi = MONTHS.findIndex(m => m.toLowerCase() === monthAbbr.toLowerCase());
  if (mi === -1) return null;
  return `${year}-${String(mi + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// Returns { date, end_date } on success, or null if the string doesn't
// cleanly match one of the four shapes above. Never guesses — null means
// "skip this row and report why."
function parseEventDate(raw) {
  if (!raw) return null;
  const str = raw.trim();

  let m = str.match(RE_CROSS_MONTH);
  if (m) {
    const [, mon1, day1, mon2, day2, year] = m;
    const date = toISO(mon1, day1, year);
    const end_date = toISO(mon2, day2, year);
    return date && end_date ? { date, end_date } : null;
  }

  m = str.match(RE_SAME_MONTH);
  if (m) {
    const [, mon, day1, day2, year] = m;
    const date = toISO(mon, day1, year);
    const end_date = toISO(mon, day2, year);
    return date && end_date ? { date, end_date } : null;
  }

  m = str.match(RE_SINGLE);
  if (m) {
    const [, mon, day, year] = m;
    const date = toISO(mon, day, year);
    return date ? { date, end_date: null } : null;
  }

  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Location — "City, ST (Venue Name)" or "City, ST" with no venue.
// The parenthetical, when present, is the venue; everything before it is
// the address/city text used for the map pin.
// ═══════════════════════════════════════════════════════════════════════════

function parseLocation(raw) {
  if (!raw?.trim()) return { city: null, venue: null };
  const trimmed = raw.trim();
  const m = trimmed.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  if (m) return { city: m[1].trim() || null, venue: m[2].trim() || null };
  return { city: trimmed, venue: null };
}

// ═══════════════════════════════════════════════════════════════════════════
// Row mapping — real sheet columns: Event Name, Location, Date, Organizer,
// Website, Description, Source.
//
// profile_events has no organizer column, so Organizer is folded into the
// description as a prefix rather than dropped or inserted into a field
// that doesn't exist.
// ═══════════════════════════════════════════════════════════════════════════

function mapRow(row) {
  const name = row['Event Name']?.trim() || '';
  const { city, venue } = parseLocation(row['Location']);
  const parsed = parseEventDate(row['Date']);

  const organizer = row['Organizer']?.trim();
  const descriptionText = row['Description']?.trim();
  const description = [
    organizer ? `Organized by ${organizer}.` : null,
    descriptionText || null,
  ].filter(Boolean).join(' ') || null;

  const event_url = row['Website']?.trim() || row['Source']?.trim() || null;

  return {
    name,
    city,
    venue,
    address: city, // same text drives the map-pin geocode fallback elsewhere
    date: parsed?.date || null,
    end_date: parsed?.end_date || null,
    description,
    event_url,
    _rawDate: row['Date']?.trim() || '',
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Dateless cleanup — events already sitting in the database with date IS
// NULL, left over from the previous import that silently wrote null for
// every row. Scan-then-confirm, never auto-deletes.
// ═══════════════════════════════════════════════════════════════════════════

function DatelessCleanup() {
  const [rows, setRows]     = useState(null); // null = not scanned yet
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState(null);

  async function scan() {
    setLoading(true);
    setResult(null);
    // Paged: the scan reports how many dateless events it found.
    let data;
    try {
      data = await fetchAllRows(() => supabase
        .from('profile_events')
        .select('id, name, address, city, user_id, created_at')
        .is('date', null));
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } catch (error) {
      alert('Scan failed: ' + error.message);
      setLoading(false);
      return;
    }
    setRows(data || []);
    setLoading(false);
  }

  async function deleteAll() {
    if (!rows?.length) return;
    if (!window.confirm(`Permanently delete ${rows.length} dateless event${rows.length !== 1 ? 's' : ''}? This cannot be undone.`)) return;

    setDeleting(true);
    const ids = rows.map(r => r.id);

    const { error: evErr } = await supabase.from('profile_events').delete().in('id', ids);
    if (evErr) {
      alert('Delete failed: ' + evErr.message);
      setDeleting(false);
      return;
    }

    // Community posts store the linked profile_events row as JSON
    // (event_data->>profile_event_id), not a real foreign key, so each
    // has to be matched individually rather than joined.
    let postErrors = 0;
    for (const id of ids) {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('event_data->>profile_event_id', String(id));
      if (error) postErrors++;
    }

    setResult({ deleted: ids.length, postErrors });
    setRows([]);
    setDeleting(false);
  }

  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 16px', lineHeight: 1.6 }}>
        Finds every <code style={{ background: 'var(--neutral-100)', padding: '1px 5px', borderRadius: 'var(--radius-sm)' }}>profile_events</code> row
        with no date, leftovers from the earlier broken import, or anything else that slipped through without one.
        Nothing is deleted until you confirm.
      </p>

      <button
        onClick={scan}
        disabled={loading}
        style={{ padding: '11px 24px', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: loading ? 'default' : 'pointer', marginBottom: 'var(--space-xl)' }}
      >
        {loading ? 'Scanning...' : 'Scan for dateless events'}
      </button>

      {rows !== null && (
        <>
          <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-800)', margin: '0 0 12px' }}>
            <strong>{rows.length}</strong> dateless event{rows.length !== 1 ? 's' : ''} found
          </p>

          {rows.length > 0 && (
            <>
              <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', marginBottom: 'var(--space-lg)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
                  <thead>
                    <tr style={{ background: 'var(--neutral-100)', position: 'sticky', top: 0 }}>
                      {['Name', 'Address', 'Created'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--neutral-500)', borderBottom: '1px solid var(--brand-sand)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.id} style={{ borderBottom: '1px solid var(--neutral-100)' }}>
                        <td style={{ padding: '7px 12px', color: 'var(--neutral-800)' }}>{r.name || '—'}</td>
                        <td style={{ padding: '7px 12px', color: 'var(--neutral-700)' }}>{r.address || r.city || '—'}</td>
                        <td style={{ padding: '7px 12px', color: 'var(--neutral-400)' }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                onClick={deleteAll}
                disabled={deleting}
                style={{ padding: '11px 24px', background: 'var(--feedback-error)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: deleting ? 'default' : 'pointer' }}
              >
                {deleting ? 'Deleting...' : `Delete ${rows.length} dateless event${rows.length !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
        </>
      )}

      {result && (
        <div style={{ marginTop: 'var(--space-lg)', padding: '12px 16px', background: 'var(--feedback-success-bg)', border: '1px solid #bbf7d0', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', color: '#166534' }}>
          ✅ Deleted {result.deleted} event{result.deleted !== 1 ? 's' : ''}
          {result.postErrors > 0 && <>, {result.postErrors} linked community post{result.postErrors !== 1 ? 's' : ''} failed to delete, check manually</>}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Main import flow
// ═══════════════════════════════════════════════════════════════════════════

export default function AdminEventImporter() {
  const [tab, setTab] = useState('import'); // 'import' | 'cleanup'

  const [mapped, setMapped]     = useState([]);
  const [skipped, setSkipped]   = useState([]); // { name, reason }
  const [rawCount, setRawCount] = useState(0);
  const [step, setStep]         = useState('idle');
  const [progress, setProgress] = useState({ done: 0, total: 0, errors: [] });

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => {
        const good = [];
        const bad  = [];

        data.forEach(row => {
          const r = mapRow(row);
          if (!r.name) {
            bad.push({ name: '(blank)', reason: 'Missing Event Name' });
            return;
          }
          if (!r.date) {
            bad.push({ name: r.name, reason: `Unparseable date: "${r._rawDate || '(empty)'}"` });
            return;
          }
          good.push(r);
        });

        setRawCount(data.length);
        setMapped(good);
        setSkipped(bad);
        setStep('preview');
      },
    });
  }

  async function handleImport() {
    setStep('importing');
    setProgress({ done: 0, total: mapped.length, errors: [] });

    const user = await getCurrentUser();
    if (!user) { alert('Not logged in'); setStep('preview'); return; }

    const errors = [];
for (let i = 0; i < mapped.length; i++) {
      const r = mapped[i];

      // Guard against re-importing the same CSV twice — matches on name +
      // date, since that's what the sheet gives us to key on. A row that
      // matches an existing event is reported, not silently skipped or
      // silently duplicated.
   const { data: dupe, error: dupeErr } = await supabase
        .from('profile_events')
        .select('id')
        .eq('name', r.name)
        .eq('date', r.date)
        .order('id', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (dupeErr) {
        errors.push({ name: r.name, reason: `Duplicate check failed: ${dupeErr.message}` });
        setProgress({ done: i + 1, total: mapped.length, errors: [...errors] });
        continue;
      }
      if (dupe) {
        errors.push({ name: r.name, reason: 'Already imported (matching name + date found)' });
        setProgress({ done: i + 1, total: mapped.length, errors: [...errors] });
        continue;
      }

      const { data: inserted, error: evErr } = await supabase
        .from('profile_events')
        .insert({
          user_id:     user.id,
          name:        r.name,
          date:        r.date,
          end_date:    r.end_date,
          time:        null,
          venue:       r.venue,
          address:     r.address,
          city:        r.city,
          country:     null,
          description: r.description,
          event_url:   r.event_url,
          role:        'attending',
          emoji:       '📅',
          color:       'var(--brand-forest)',
        })
        .select('id')
        .maybeSingle();

      if (evErr) {
        errors.push({ name: r.name, reason: evErr.message });
        setProgress({ done: i + 1, total: mapped.length, errors: [...errors] });
        continue;
      }

      const eventData = {
        title:             r.name,
        date:              r.date,
        end_date:          r.end_date,
        city:              r.city,
        venue:             r.venue,
        address:           r.address,
        event_url:         r.event_url,
        emoji:             '📅',
        color:             'var(--brand-forest)',
        role:              'attending',
        imported:          true,
        profile_event_id:  inserted?.id || null,
      };

      const { error: postErr } = await supabase.from('posts').insert({
        user_id:    user.id,
        post_type:  'event',
        content:    `📍 ${r.name}${r.city ? `, ${r.city}` : ''}`,
        event_data: eventData,
      });

      if (postErr) {
        errors.push({ name: r.name, reason: `Post failed: ${postErr.message}` });
      }

      setProgress({ done: i + 1, total: mapped.length, errors: [...errors] });
    }

    setStep('done');
  }

  function reset() {
    setMapped([]); setSkipped([]); setRawCount(0); setStep('idle');
    setProgress({ done: 0, total: 0, errors: [] });
  }

  const successCount = progress.total - progress.errors.length;

  return (
    <div style={{ maxWidth: 900, padding: '32px 24px', fontFamily: 'var(--font-ui, system-ui, sans-serif)' }}>

      <div style={{ marginBottom: 'var(--space-xxl)' }}>
        <h2 style={{ fontSize: 'var(--font-size-xxl)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>
          📥 Events
        </h2>
      </div>

      {/* Tabs — import and cleanup share this surface */}
      <div style={{ display: 'flex', gap: 'var(--space-xs)', marginBottom: 'var(--space-xxl)', borderBottom: '1px solid var(--brand-sand)' }}>
        {[['import', 'Import from sheet'], ['cleanup', 'Clean up dateless events']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 'var(--font-size-base)', fontWeight: tab === id ? 700 : 500,
              color: tab === id ? 'var(--neutral-800)' : 'var(--neutral-400)',
              borderBottom: tab === id ? '2px solid var(--neutral-800)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'cleanup' && <DatelessCleanup />}

      {tab === 'import' && (
        <>
          <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 20px' }}>
            Upload a CSV from Google Sheets. Each row creates a{' '}
            <code style={{ background: 'var(--neutral-100)', padding: '1px 5px', borderRadius: 'var(--radius-sm)' }}>profile_events</code>{' '}
            entry and a community post. The admin is set as attending, not as the organizer.
            Only the "Upcoming" tab of the sheet should be exported; the annual-events tab is a different shape and isn't handled here.
          </p>

          <div style={{ background: '#fefce8', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-10)', padding: '12px 16px', marginBottom: 'var(--space-xxl)', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', lineHeight: 1.7 }}>
            <strong style={{ color: 'var(--neutral-800)' }}>Expected columns: </strong>
            Event Name, Location, Date, Organizer, Website, Description, Source
          </div>

          {step === 'idle' && (
            <label
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 'var(--space-10)', padding: '48px 24px',
                border: '2px dashed var(--neutral-300)', borderRadius: 14,
                cursor: 'pointer', background: 'var(--brand-paper)', transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#2D4A3E'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#d6d3d1'}
            >
              <span style={{ fontSize: 36 }}>📂</span>
              <span style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--neutral-800)' }}>Click to upload CSV</span>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>Google Sheets → File → Download → CSV</span>
              <input type="file" accept=".csv" onChange={handleFile} style={{ display: 'none' }} />
            </label>
          )}

          {step === 'preview' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-14)' }}>
                <p style={{ margin: 'var(--space-none)', fontSize: 'var(--font-size-md)', color: 'var(--neutral-800)' }}>
                  <strong>{mapped.length}</strong> of <strong>{rawCount}</strong> rows ready to import
                </p>
                <button onClick={reset} style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', cursor: 'pointer', textDecoration: 'underline' }}>
                  ✕ Clear
                </button>
              </div>

              {/* Skip report — every row that won't parse, and why. Never guessed. */}
              {skipped.length > 0 && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--radius-10)', padding: '14px 16px', marginBottom: 'var(--space-xl)' }}>
                  <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: '#991b1b', margin: '0 0 8px' }}>
                    ⚠ {skipped.length} row{skipped.length !== 1 ? 's' : ''} skipped, no date guessed
                  </p>
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {skipped.map((s, i) => (
                      <p key={i} style={{ fontSize: 11.5, color: '#7f1d1d', margin: '0 0 3px', lineHeight: 1.5 }}>
                        • <strong>{s.name}</strong>, {s.reason}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ overflowX: 'auto', marginBottom: 'var(--space-xl)', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
                  <thead>
                    <tr style={{ background: 'var(--neutral-100)' }}>
                      {['Event Name', 'Location', 'Venue', 'Date', 'End Date', 'Link'].map(h => (
                        <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--neutral-500)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--brand-sand)' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {mapped.slice(0, 8).map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--neutral-100)' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--neutral-800)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--neutral-700)' }}>{r.city || '—'}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--neutral-700)' }}>{r.venue || '—'}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--neutral-700)' }}>{r.date || '—'}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--neutral-700)' }}>{r.end_date || '—'}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--neutral-700)' }}>
                          {r.event_url
                            ? <a href={r.event_url} target="_blank" rel="noreferrer" style={{ color: 'var(--brand-forest)', fontWeight: 600 }}>🔗 link</a>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {mapped.length > 8 && (
                  <p style={{ textAlign: 'center', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', padding: '10px 0', margin: 'var(--space-none)' }}>
                    + {mapped.length - 8} more rows not shown in preview
                  </p>
                )}
              </div>

              <button
                onClick={handleImport}
                style={{
                  padding: '13px 32px', background: 'var(--brand-forest)', color: 'var(--brand-white)',
                  border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-md)', fontWeight: 700,
                  cursor: 'pointer', transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#3d6b57'}
                onMouseLeave={e => e.currentTarget.style.background = '#2D4A3E'}
              >
                Import {mapped.length} events →
              </button>
            </>
          )}

          {step === 'importing' && (
            <div style={{ padding: '40px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-10)' }}>
                <p style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>Importing events...</p>
                <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>{progress.done} / {progress.total}</p>
              </div>
              <div style={{ background: 'var(--brand-sand)', borderRadius: 99, height: 10, overflow: 'hidden', marginBottom: 'var(--space-lg)' }}>
                <div style={{
                  height: '100%', borderRadius: 99, background: 'var(--brand-forest)',
                  width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`,
                  transition: 'width 0.2s',
                }} />
              </div>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Don't close this page</p>
            </div>
          )}

          {step === 'done' && (
            <div style={{
              padding: '28px 24px', borderRadius: 14,
              background: progress.errors.length === 0 ? 'var(--feedback-success-bg)' : '#fffbeb',
              border: `1px solid ${progress.errors.length === 0 ? '#bbf7d0' : '#fde68a'}`,
            }}>
              <p style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>
                {progress.errors.length === 0 ? '✅' : '⚠️'} Import complete
              </p>
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 20px' }}>
                <strong>{successCount}</strong> event{successCount !== 1 ? 's' : ''} imported successfully
                {progress.errors.length > 0 && (
                  <>, <strong style={{ color: 'var(--feedback-warning)' }}>{progress.errors.length}</strong> failed</>
                )}
                {skipped.length > 0 && (
                  <>, <strong style={{ color: '#991b1b' }}>{skipped.length}</strong> skipped before import (unparseable date or missing name)</>
                )}
              </p>

              {progress.errors.length > 0 && (
                <div style={{ marginBottom: 'var(--space-xl)', padding: '12px 16px', background: 'var(--brand-white)', borderRadius: 'var(--radius-10)', border: '1px solid var(--feedback-warning-border)' }}>
                  <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--feedback-warning)', margin: '0 0 10px' }}>Failed rows:</p>
                  {progress.errors.map((err, i) => (
                    <p key={i} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '0 0 4px' }}>
                      • <strong>{err.name}</strong>, {err.reason}
                    </p>
                  ))}
                </div>
              )}

              <button
                onClick={reset}
                style={{ padding: '10px 24px', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'pointer' }}
              >
                Import another file
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}